import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { z } from 'zod';
import {
  Button,
  RHFForm,
  Text,
  TextField,
  CardNumberField,
  CardExpiryField,
  CardCvcField,
  CardZipField,
  cardNumberZodSchema,
  cardExpiryZodSchema,
  cardCvcZodSchema,
  cardZipZodSchema,
  cardCvcMatchesBrand,
} from 'shared-components';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { BCMShortWizardShell, BCMShortSubmitButton } from '@components/BCMShort/WizardShell';
import { useApplyWizardContext } from '../../context/ApplyWizardContext';
import ensureLoggedIn from '../../hooks/ensureLoggedIn';
import { getBcmBillingConfig, BcmBillingConfigResponse } from '../../api/getBcmBillingConfig';
import { postBcmPaymentMethod } from '../../api/postBcmPaymentMethod';
import { tokenizeCard, CardTokenizationError } from '@utils/tokenizeCard';
import { flowStep, flowStepComplete } from '@utils/gaFlow';

/**
 * Optional post-signup add-a-card step for the /bcm flow — the Apply
 * counterpart of volt's BCMSignupWizard AddCardStep. Fully skippable:
 * the BCS signup already succeeded — this only decides whether the trial
 * auto-converts at the end of the free month.
 *
 * Gated SERVER-SIDE via billing-config.applyCardStepEnabled (see
 * node-backend config/billing.ts isBcsBillingApplyCardStepEnabled): flag
 * off, config fetch failure, or no billing at all ⇒ the step forwards
 * straight to /bcm/app/contact-soon, byte-identical to the old flow.
 *
 * The card fields live in a local RHFForm, NOT the persisted redux
 * 'application' form — card data must never enter redux state. The PAN is
 * tokenized browser → provider (utils/tokenizeCard); only the one-time
 * token reaches our backend.
 */

// Same composition as volt's CardCaptureForm: the shared Card*Field zod
// schemas own the per-field rules (Luhn + brand length for the PAN,
// not-expired MM/YY, brand-aware CVC, strict 5-digit US ZIP — per the
// QuickBooks Payments card contract), plus the cross-field CVC/brand check.
const cardSchema = z
  .object({
    cardName: z.string().min(2, 'Please enter the name on the card.'),
    cardNumber: cardNumberZodSchema,
    cardExpiry: cardExpiryZodSchema,
    cardCvc: cardCvcZodSchema,
    cardZip: cardZipZodSchema,
  })
  .refine((values) => cardCvcMatchesBrand(values.cardNumber, values.cardCvc), {
    path: ['cardCvc'],
    error: 'The security code length does not match this card type.',
  });

type CardValues = z.infer<typeof cardSchema>;

const splitExpiry = (expiry: string): { expMonth: string; expYear: string } => {
  const [month, yearRaw] = expiry.split('/').map((part) => part.trim());
  const expYear = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
  return { expMonth: month, expYear };
};

const BCMShortAddCardComponent = ({ business_id }: { business_id?: string }) => {
  const history = useHistory();
  const { nextRoute } = useApplyWizardContext();
  const [{ data: configResponse, loading: configLoading, error: configError }, fetchBillingConfig] =
    getBcmBillingConfig();

  // Apply's useCancelableAxios forces { manual: true } on EVERY hook, so the
  // config must be fetched explicitly — without this the request never fires,
  // `loading` stays false and the settle-guard below would read the missing
  // config as "flag off" and bounce to contact-soon instantly (staging,
  // 2026-09-23).
  useEffect(() => {
    fetchBillingConfig().catch(() => {
      // settle-guard reads configError; nothing to do here
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [, attachPaymentMethod] = postBcmPaymentMethod();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const config = (configResponse as BcmBillingConfigResponse | undefined)?.data;
  // Settled = the explicit fetch above produced a response or an error.
  const configSettled = !!configResponse || !!configError;
  const stepEnabled = !configError && !!config?.applyCardStepEnabled && !!business_id;

  useEffect(() => {
    flowStep('apply_bcm', 'add_card');
  }, []);

  // Flag off / config unavailable / no account id ⇒ old behavior. `replace`
  // (not push) so the browser back button never lands on this decision hop.
  useEffect(() => {
    if (configLoading || !configSettled) return;
    if (!stepEnabled) {
      history.replace('/bcm/app/contact-soon');
    }
  }, [configLoading, configSettled, stepEnabled]);

  if (configLoading || !configSettled || !config || !stepEnabled || !business_id) {
    return (
      <BCMShortWizardShell currentStepIndex={2}>
        <div className="flex items-center justify-center w-full py-[60px]">
          <Spinner />
        </div>
      </BCMShortWizardShell>
    );
  }

  const priceMonthly = config.priceMonthly ?? '14.99';
  const trialMonths = config.trialMonths ?? 1;

  const handleSubmit = async (values: CardValues) => {
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const { expMonth, expYear } = splitExpiry(values.cardExpiry);
      const token = await tokenizeCard(
        {
          name: values.cardName,
          number: values.cardNumber,
          expMonth,
          expYear,
          cvc: values.cardCvc,
          postalCode: values.cardZip,
        },
        { tokenizeUrl: config.tokenizeUrl, clientId: config.clientId }
      );
      await attachPaymentMethod({ data: { accountPublicId: business_id, cardToken: token } });
      flowStepComplete('apply_bcm', 'add_card');
      nextRoute?.();
    } catch (error) {
      const backendMessage = (error as { response?: { data?: { userMessage?: string; message?: string } } })?.response
        ?.data;
      setErrorMessage(
        error instanceof CardTokenizationError
          ? error.message
          : backendMessage?.userMessage ??
              backendMessage?.message ??
              'Something went wrong saving your card. Please try again.'
      );
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    flowStepComplete('apply_bcm', 'add_card_skipped');
    nextRoute?.();
  };

  return (
    <BCMShortWizardShell currentStepIndex={2}>
      <Helmet>
        <title>One Optional Step</title>
      </Helmet>

      <div className="w-full max-w-[592px] mx-auto flex flex-col items-center gap-[20px]">
        <div className="flex items-center justify-center py-[20px] w-full">
          <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
            You&apos;re in — one optional step
          </Text>
        </div>

        <Text variant="body-general" className="!text-[16px] !leading-[24px] text-center max-w-[480px]">
          {trialMonths === 1 ? 'Your first month is free.' : `Your first ${trialMonths} months are free.`} Add a card
          now and your subscription continues automatically at ${priceMonthly}/month when{' '}
          {trialMonths === 1 ? 'it ends' : 'they end'} — we&apos;ll email you 7 days before the first charge, and you
          can cancel anytime. Prefer to decide later? Just skip.
        </Text>

        {/* Neutralize the Apply-wide clamps from shared globals.css
            (.apply .tento-form-element max-width 368px / .apply
            .btn-component-cta max-width 280px) inside this form only, so
            every row and the submit stretch to the same 480px width —
            byte-identical layout to volt's CardCaptureForm. */}
        <div className="w-full max-w-[480px] [&_.tento-form-element]:!max-w-none [&_.btn-component-cta]:!max-w-none">
          <RHFForm
            schema={cardSchema}
            initialValues={{ cardName: '', cardNumber: '', cardExpiry: '', cardCvc: '', cardZip: '' }}
            onSubmit={handleSubmit}
            // Same row rhythm as volt's CardCaptureForm: plain flex column
            // with gap-[10px] — NOT Stack, whose grid swallows the gap and
            // glues the rows together.
            containerProps={{ className: 'flex flex-col w-full gap-[10px]' }}
            className="w-full"
          >
            <TextField
              name="cardName"
              label="Name on card"
              variant="transparent-black"
              className="!bg-dawn3"
              autoComplete="cc-name"
            />
            <CardNumberField name="cardNumber" label="Card number" variant="transparent-black" className="!bg-dawn3" />
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-[10px] w-full">
              <CardExpiryField
                name="cardExpiry"
                label="Expiry (MM/YY)"
                variant="transparent-black"
                className="!bg-dawn3"
              />
              <CardCvcField
                name="cardCvc"
                label="CVC"
                cardNumberFieldName="cardNumber"
                variant="transparent-black"
                className="!bg-dawn3"
              />
              <CardZipField name="cardZip" label="ZIP" variant="transparent-black" className="!bg-dawn3" />
            </div>
            {errorMessage && (
              <Text variant="error" className="!text-[13px]">
                {errorMessage}
              </Text>
            )}
            <Text variant="muted" className="!text-[12px]">
              Nothing is charged today.
            </Text>
            <BCMShortSubmitButton saving={submitting}>Save card</BCMShortSubmitButton>
          </RHFForm>
        </div>

        {/* Text link, not a second pill button — matches volt's AddCardStep /
            skip-Plaid-connect pattern, underline kept visible. */}
        <Button
          variant="text"
          onClick={handleSkip}
          disabled={submitting}
          className="px-[40px] py-[10px] underline underline-offset-4"
        >
          Skip for now
        </Button>
      </div>
    </BCMShortWizardShell>
  );
};

const selector = formValueSelector('application');

const mapStateToProps = (state: RootState) => ({
  business_id: selector(state, 'business_id') || state.user.accountId,
});

export const BCMShortAddCard = ensureLoggedIn(connect(mapStateToProps)(BCMShortAddCardComponent), {
  requireBusinessId: true,
});
